import { useState } from '#imports'
import { type Ref, computed, ref, shallowRef } from 'vue'

import type {
  MultipleQueriesQuery,
  SearchResponse,
} from '@algolia/client-search'
import type {
  AlgoliaLink,
  AlgoliaProduct,
} from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'
import type { SearchBarConfiguration } from '@backmarket/http-api/src/api-specs-search-reco/search/searchConfiguration'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { useTestChallenge } from '@backmarket/nuxt-layer-algolia/useRedirectToTestChallenge'
import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { useVisitorId } from '@backmarket/nuxt-module-identification/useVisitorId'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import algoliasearch from 'algoliasearch'

import type { SearchTrackingData } from '~/scopes/product-list/composables/useProductsSearch/useProductsSearch.types'
import {
  type Link,
  algoliaHitToLink,
  algoliaHitToProduct,
} from '~/scopes/search/algolia/algoliaFunctions'

import {
  MAX_PRODUCTS_HITS,
  MAX_SUGGESTIONS_HITS,
} from '../../SearchBar.constants'

export interface UseAlgoliaSearchParams {
  query: Ref<string>
  configuration: Ref<SearchBarConfiguration | undefined>
}

export function useAlgoliaSearch({
  query,
  configuration,
}: UseAlgoliaSearchParams) {
  const productsHits = shallowRef<Product[]>([])
  const suggestionsHits = shallowRef<Link[]>([])
  const isFetchingProductAndSuggestions = ref(false)

  const logger = useLogger()
  const algoliaSearchOption = useAlgoliaSearchOptions('search', 'searchbar')
  const visitorId = useVisitorId()
  const { redirectToTestChallengePage, isTestChallengeError } =
    useTestChallenge()
  const searchTrackingData = useState<SearchTrackingData>('search-query')

  const algoliaClient = computed(() => {
    if (!configuration.value) return null

    return algoliasearch('dummy_app_id', 'dummy_key', algoliaSearchOption)
  })

  const searchProductsAndSuggestions = async () => {
    if (!algoliaClient.value || !query.value || !configuration.value) return

    isFetchingProductAndSuggestions.value = true

    const queries: MultipleQueriesQuery[] = [
      {
        indexName: configuration.value.backboxWinners.indexes.active.name,
        query: query.value,
        params: removeEmptyValuesInObject({
          filters: configuration.value.backboxWinners.complexFilter,
          hitsPerPage: MAX_PRODUCTS_HITS,
          clickAnalytics: true,
        }),
      },
      {
        indexName: configuration.value.landingPages.indexes.active.name,
        query: query.value,
        params: removeEmptyValuesInObject({
          highlightPreTag: '<span class="body-1-bold text-static-default-hi">',
          highlightPostTag: '</span>',
          clickAnalytics: true,
          userToken: visitorId,
          filters: configuration.value.landingPages.complexFilter,
          hitsPerPage: MAX_SUGGESTIONS_HITS,
        }),
      },
    ]

    try {
      const { results } = await algoliaClient.value.search(queries)

      const productsResult = results[0] as SearchResponse<AlgoliaProduct>

      searchTrackingData.value = {
        id: productsResult.queryID,
        index: configuration.value.backboxWinners.indexes.active.name,
      }

      productsHits.value = productsResult.hits.map((hit) => {
        return algoliaHitToProduct(hit)
      })

      const suggestionsResult = results[1] as SearchResponse<AlgoliaLink>
      suggestionsHits.value = suggestionsResult.hits
        .filter(
          (hit) => !(hit.link.type === 'internal' && !hit.link.params.slugV2),
        )
        .map((hit) => {
          return algoliaHitToLink(hit)
        })
    } catch (error) {
      logger.info('[S&R] Impossible trigger Search Bar search', {
        type: 'API_REQUEST_ERROR',
        owners: ['bot-squad-search-and-recommendation-front'],
        cause: error,
      })

      if (isTestChallengeError(error)) {
        redirectToTestChallengePage()
      }
    } finally {
      isFetchingProductAndSuggestions.value = false
    }
  }

  return {
    productsHits,
    suggestionsHits,
    isFetchingProductAndSuggestions,
    searchProductsAndSuggestions,
  }
}
